import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  CubeIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { documentService, stockHistoryService } from '../../api/services';

type NotifKind = 'overdue' | 'paid' | 'stock' | 'system';

interface Notif {
  id: string;
  kind: NotifKind;
  title: string;
  body: string;
  time: string;
  link?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const TABS: { id: 'all' | NotifKind; label: string }[] = [
  { id: 'all', label: 'Tout' },
  { id: 'overdue', label: 'Factures' },
  { id: 'stock', label: 'Stock' },
  { id: 'system', label: 'Système' },
];

const iconFor: Record<NotifKind, React.FC<{ className?: string }>> = {
  overdue: ExclamationCircleIcon,
  paid: CheckCircleIcon,
  stock: CubeIcon,
  system: Cog6ToothIcon,
};

const colorFor: Record<NotifKind, string> = {
  overdue: 'text-red-600 bg-red-50',
  paid: 'text-emerald-600 bg-emerald-50',
  stock: 'text-amber-600 bg-amber-50',
  system: 'text-blue-600 bg-blue-50',
};

export function NotificationsPanel({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'all' | NotifKind>('all');
  const [notifs, setNotifs] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(false);

  // Synthesize notifications from existing endpoints. When a dedicated /notifications
  // backend lands, swap this out — the panel UI stays the same.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const [docs, rupture] = await Promise.all([
          documentService.getAll({ size: 200 }).then((p) => p.content).catch(() => []),
          stockHistoryService.rupture().catch(() => []),
        ]);
        const out: Notif[] = [];
        const today = new Date();
        for (const d of docs) {
          const dd: any = d;
          if (dd.statutPaiement === 'PAYEE' && dd.updatedAt) {
            out.push({
              id: `paid-${dd.id}`, kind: 'paid',
              title: `Facture ${dd.numero} payée`,
              body: `${dd.clientRaisonSociale || ''} — ${Number(dd.netAPayer || 0).toLocaleString('fr-TN')} TND`,
              time: dd.updatedAt, link: `/documents/view/${dd.id}`,
            });
          }
          if (dd.dateEcheance && dd.statutPaiement !== 'PAYEE') {
            const due = new Date(dd.dateEcheance);
            if (due < today) {
              out.push({
                id: `overdue-${dd.id}`, kind: 'overdue',
                title: `Facture ${dd.numero} en retard`,
                body: `${dd.clientRaisonSociale || ''} — échéance ${dd.dateEcheance}`,
                time: dd.dateEcheance, link: `/documents/view/${dd.id}`,
              });
            }
          }
        }
        for (const a of rupture as any[]) {
          out.push({
            id: `stock-${a.id}`, kind: 'stock',
            title: `Stock bas : ${a.designation}`,
            body: `Stock actuel ${a.stockActuel ?? 0} (min. ${a.stockMinimum ?? 0})`,
            time: new Date().toISOString(), link: '/stock/mouvements',
          });
        }
        out.sort((a, b) => (b.time || '').localeCompare(a.time || ''));
        setNotifs(out.slice(0, 50));
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const filtered = useMemo(
    () => (tab === 'all' ? notifs : notifs.filter((n) => n.kind === tab || (tab === 'overdue' && n.kind === 'paid'))),
    [notifs, tab],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div
        className="absolute top-14 right-4 w-[min(420px,calc(100vw-2rem))] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Activité</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Fermer"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="flex border-b border-gray-100 px-2">
          {TABS.map((tt) => (
            <button
              key={tt.id}
              type="button"
              onClick={() => setTab(tt.id)}
              className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition ${
                tab === tt.id
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tt.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-400">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">Pas d'activité pour le moment.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((n) => {
                const Icon = iconFor[n.kind];
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (n.link) navigate(n.link);
                        onClose();
                      }}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition"
                    >
                      <span className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full ${colorFor[n.kind]}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{n.title}</div>
                        <div className="text-xs text-gray-500 truncate">{n.body}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationsPanel;
